/**
 * **Papan antrean tamu** — layar yang menghadap ruang makan (Fase 4).
 *
 * ### ⭐ Ini satu-satunya layar yang dibaca ORANG LUAR
 * Semua shell lain dibaca staf. Yang ini ditatap tamu dari seberang ruangan, sambil berdiri, sering
 * dari sudut. Akibatnya tiga hal mengikat:
 * 1. **Angka sebesar mungkin** — nomor harus terbaca dari 8–10 meter, bukan sekadar "besar".
 * 2. **Dua kolom, bukan daftar** — "sedang disiapkan" vs "silakan diambil" adalah satu-satunya
 *    pertanyaan yang dibawa tamu ke layar ini. Menggabungkannya jadi satu daftar berstatus kecil
 *    memaksa orang membaca dua kali untuk satu jawaban.
 * 3. **⛔ TAK ADA harga, tak ada isi pesanan.** Papan ini dibaca oleh orang yang bukan pemiliknya;
 *    apa yang dipesan seseorang bukan urusan seisi ruangan.
 *
 * ### ⛔⛔ Layar yang menyala TANPA PENJAGA
 * Tak ada yang mem-PIN papan ini — ia dipasang sekali lalu menyala berbulan-bulan. Karena itu ia
 * memakai token PERANGKAT, bukan sesi staf: sesi staf mati tengah malam dan paginya antrean tamu
 * menatap layar batu. Kanal dorong pun menerima token mesin justru untuk kasus ini.
 */
import { useCallback, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { api, type QueueBoard } from '../api/client';
import { openLive, type LiveState } from '../api/live';
import { storedToken } from '../api/session';

export interface QueueShellProps {
  readonly outletCode: string;
  readonly businessDate: string;
}

export function QueueShell(p: QueueShellProps) {
  const { t } = useTranslation();
  const [board, setBoard] = useState<QueueBoard | undefined>(undefined);
  const [failed, setFailed] = useState(false);
  const [liveState, setLiveState] = useState<LiveState>('connecting');

  const load = useCallback(async () => {
    try {
      setBoard(await api.queueBoard(p.outletCode, p.businessDate));
      setFailed(false);
    } catch {
      /*
       * ⚠️ Papan TIDAK dikosongkan saat gagal memuat. Nomor yang lenyap membuat tamu mengira
       * pesanannya sudah lewat dan maju ke konter — dan konter yang sedang sibuk jadi berhenti
       * untuk menjelaskan sesuatu yang tak pernah terjadi.
       */
      setFailed(true);
    }
  }, [p.outletCode, p.businessDate]);

  useEffect(() => {
    void load();
    const token = storedToken();
    if (token === undefined) return;
    const channel = openLive({
      outletCode: p.outletCode,
      /* ⭐ Token PERANGKAT — lihat catatan di kepala berkas soal layar tanpa penjaga. */
      staffToken: token,
      onNudge: (kind) => { if (kind === undefined || kind === 'kitchen') void load(); },
      onState: setLiveState,
    });
    return () => { channel.close(); };
  }, [load, p.outletCode]);

  /*
   * ⛔ Outlet tanpa tiket dapur: dikatakan APA ADANYA, bukan dibiarkan kosong selamanya. Papan
   * kosong terbaca sebagai "tak ada pesanan", lalu layarnya dicabut orang karena dikira rusak (P8).
   */
  if (board !== undefined && !board.hasTickets) {
    return (
      <div className="queue" data-testid="queue">
        <p className="empty" data-testid="queue-unsupported">{t('queue.noTickets')}</p>
      </div>
    );
  }

  const preparing = board?.preparing ?? [];
  const ready = board?.ready ?? [];

  return (
    <div className="queue" data-testid="queue">
      {failed ? <p className="error" data-testid="queue-error">{t('queue.loadFailed')}</p> : null}
      {liveState === 'degraded' ? (
        <p className="warn" data-testid="queue-degraded">{t('queue.degraded')}</p>
      ) : null}

      <section className="queue-col preparing" data-testid="queue-preparing">
        <h2>{t('queue.preparing')}</h2>
        <div className="queue-numbers">
          {preparing.map((e) => (
            <span key={e.orderId} className="queue-no" data-testid={`queue-prep-${e.label}`}>
              {e.label}
            </span>
          ))}
        </div>
      </section>

      {/* ⭐ Kolom "siap" diberi bobot visual lebih: itulah yang dicari mata tamu tiap kali menoleh. */}
      <section className="queue-col ready" data-testid="queue-ready">
        <h2>{t('queue.ready')}</h2>
        <div className="queue-numbers">
          {ready.map((e) => (
            <span key={e.orderId} className="queue-no" data-testid={`queue-ready-${e.label}`}>
              {e.label}
            </span>
          ))}
        </div>
      </section>
    </div>
  );
}
